import React, {
    useCallback,
    useEffect,
    useMemo,
    useState
} from "react";

import {
    getDeliveryModes,
    calculateQuote,
    createFulfillmentRequest,
    getFulfillmentRequest,
    acceptBid,
    updateDeliveryStatus
} from "../services/FulfillmentService";


const STATUS_LABELS = {
    pending: "Pending",
    bidding: "Bidding",
    assigned: "Assigned",
    picked_up: "Picked Up",
    in_transit: "In Transit",
    delivered: "Delivered",
    cancelled: "Cancelled"
};


function formatCurrency(value) {
    const amount = Number(value || 0);

    return new Intl.NumberFormat(
        "en-IN",
        {
            style: "currency",
            currency: "INR",
            maximumFractionDigits: 0
        }
    ).format(amount);
}


function formatStatus(status) {
    if (!status) {
        return "Unknown";
    }

    return (
        STATUS_LABELS[status] ||
        String(status)
            .replace(/_/g, " ")
            .replace(/\b\w/g, char => char.toUpperCase())
    );
}


function getModeLabel(mode) {
    if (!mode) {
        return "Delivery";
    }

    if (typeof mode === "string") {
        return mode;
    }

    return (
        mode.label ||
        mode.name ||
        mode.title ||
        mode.id ||
        "Delivery"
    );
}


function getModeIcon(mode) {
    if (!mode) {
        return "🚚";
    }

    if (typeof mode === "string") {
        const value = mode.toLowerCase();

        if (
            value.includes("bicycle") ||
            value.includes("cycle")
        ) {
            return "🚲";
        }

        if (
            value.includes("bike") ||
            value.includes("motor")
        ) {
            return "🏍";
        }

        if (
            value.includes("car") ||
            value.includes("van")
        ) {
            return "🚗";
        }

        if (value.includes("truck")) {
            return "🚚";
        }

        return "🚚";
    }

    return (
        mode.icon ||
        mode.emoji ||
        getModeIcon(
            mode.label ||
            mode.name ||
            ""
        )
    );
}


function getRequestId(item) {
    return (
        item?.id ||
        item?.requestId ||
        item?.request_id
    );
}


function getDeliveryId(item) {
    return (
        item?.deliveryId ||
        item?.delivery_id ||
        item?.id
    );
}


function getBidId(bid) {
    return (
        bid?.id ||
        bid?.bidId ||
        bid?.bid_id
    );
}


function getBids(item) {
    if (!item) {
        return [];
    }

    return (
        item.bids ||
        item.bid ||
        item.responses ||
        []
    );
}


function getRequestStatus(item) {
    return (
        item?.status ||
        item?.requestStatus ||
        item?.request_status ||
        "pending"
    );
}


function getDeliveryStatus(item) {
    return (
        item?.status ||
        item?.deliveryStatus ||
        item?.delivery_status ||
        "assigned"
    );
}


function getPartnerName(bid) {
    return (
        bid?.partner?.name ||
        bid?.partner?.fullName ||
        bid?.partner?.full_name ||
        bid?.partnerName ||
        bid?.partner_name ||
        "Delivery Partner"
    );
}


function getPartnerRating(bid) {
    return (
        bid?.partner?.rating ??
        bid?.partnerRating ??
        bid?.partner_rating ??
        null
    );
}


function getBidAmount(bid) {
    return (
        bid?.amount ??
        bid?.payout ??
        bid?.offer ??
        bid?.price ??
        0
    );
}


function getBidEta(bid) {
    return (
        bid?.etaMinutes ??
        bid?.eta_minutes ??
        bid?.eta ??
        null
    );
}


function getDeliveryRequestId(item) {
    return (
        item?.requestId ||
        item?.request_id ||
        item?.fulfillmentRequestId ||
        item?.fulfillment_request_id
    );
}


/*
==================================================
COORDINATE NORMALIZATION
==================================================

The UI may contain either:

22.5680

or:

22.5680° N, 88.3711° E

The backend needs numeric latitude/longitude
values, so normalize both plain numeric values
and formatted coordinate strings.
==================================================
*/

function parseCoordinate(value) {
    if (typeof value === "number") {
        return Number.isFinite(value)
            ? value
            : undefined;
    }

    if (typeof value !== "string") {
        return undefined;
    }

    const text = value.trim();

    if (!text) {
        return undefined;
    }

    const direct = Number(text);

    if (Number.isFinite(direct)) {
        return direct;
    }

    const match = text.match(
        /[-+]?\d+(?:\.\d+)?/
    );

    if (!match) {
        return undefined;
    }

    const number = Number(match[0]);

    if (!Number.isFinite(number)) {
        return undefined;
    }

    if (
        /S/i.test(text) ||
        /W/i.test(text)
    ) {
        return -Math.abs(number);
    }

    return number;
}


function parseCoordinatePair(value) {
    if (typeof value !== "string") {
        return null;
    }

    const matches = value.match(
        /[-+]?\d+(?:\.\d+)?/g
    );

    if (
        !matches ||
        matches.length < 2
    ) {
        return null;
    }

    let lat = Number(matches[0]);
    let lng = Number(matches[1]);

    if (
        !Number.isFinite(lat) ||
        !Number.isFinite(lng)
    ) {
        return null;
    }

    if (/S/i.test(value)) {
        lat = -Math.abs(lat);
    }

    if (/N/i.test(value)) {
        lat = Math.abs(lat);
    }

    if (/W/i.test(value)) {
        lng = -Math.abs(lng);
    }

    if (/E/i.test(value)) {
        lng = Math.abs(lng);
    }

    return {
        lat,
        lng
    };
}


function getCoordinates(
    latValue,
    lngValue
) {
    const latText =
        typeof latValue === "string"
            ? latValue.trim()
            : latValue;

    const lngText =
        typeof lngValue === "string"
            ? lngValue.trim()
            : lngValue;

    const pairFromLat =
        parseCoordinatePair(
            latText
        );

    if (
        pairFromLat &&
        (
            !lngText ||
            parseCoordinate(lngText) ===
                undefined
        )
    ) {
        return pairFromLat;
    }

    const pairFromLng =
        parseCoordinatePair(
            lngText
        );

    if (
        pairFromLng &&
        (
            !latText ||
            parseCoordinate(latText) ===
                undefined
        )
    ) {
        return pairFromLng;
    }

    const lat =
        parseCoordinate(latText);

    const lng =
        parseCoordinate(lngText);

    if (
        !Number.isFinite(lat) ||
        !Number.isFinite(lng)
    ) {
        return null;
    }

    return {
        lat,
        lng
    };
}


const cardStyle = {
    background: "#fff",
    border: "1px solid #e7e7e7",
    borderRadius: 18,
    padding: 22,
    boxSizing: "border-box"
};


const inputStyle = {
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid #dedede",
    borderRadius: 11,
    padding: "12px 13px",
    fontSize: 14,
    outline: "none",
    background: "#fff"
};


const labelStyle = {
    display: "block",
    marginBottom: 7,
    fontSize: 13,
    fontWeight: 700,
    color: "#444"
};


const primaryButtonStyle = {
    border: 0,
    borderRadius: 11,
    padding: "12px 17px",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    background: "#111",
    color: "#fff"
};


const secondaryButtonStyle = {
    border: "1px solid #ddd",
    borderRadius: 11,
    padding: "11px 16px",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    background: "#fff",
    color: "#111"
};


function StatCard({
    label,
    value
}) {
    return (
        <div
            style={{
                ...cardStyle,
                minWidth: 0
            }}
        >
            <div
                style={{
                    fontSize: 13,
                    color: "#777",
                    marginBottom: 10
                }}
            >
                {label}
            </div>

            <div
                style={{
                    fontSize: 30,
                    lineHeight: 1,
                    fontWeight: 800,
                    color: "#111"
                }}
            >
                {value}
            </div>
        </div>
    );
}


function EmptyState({
    title,
    text
}) {
    return (
        <div
            style={{
                padding: "38px 20px",
                textAlign: "center",
                color: "#777"
            }}
        >
            <div
                style={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: "#333",
                    marginBottom: 7
                }}
            >
                {title}
            </div>

            <div
                style={{
                    fontSize: 14
                }}
            >
                {text}
            </div>
        </div>
    );
}


export default function BrandFulfillment({
    brand
}) {
    const [
        activeSection,
        setActiveSection
    ] = useState("overview");

    const [
        deliveryModes,
        setDeliveryModes
    ] = useState([]);

    const [
        requests,
        setRequests
    ] = useState([]);

    const [
        activeDeliveries,
        setActiveDeliveries
    ] = useState([]);

    const [
        history,
        setHistory
    ] = useState([]);

    const [
        selectedRequest,
        setSelectedRequest
    ] = useState(null);

    const [
        loading,
        setLoading
    ] = useState(true);

    const [
        creating,
        setCreating
    ] = useState(false);

    const [
        quoting,
        setQuoting
    ] = useState(false);

    const [
        error,
        setError
    ] = useState("");

    const [
        success,
        setSuccess
    ] = useState("");

    const [
        quote,
        setQuote
    ] = useState(null);

    const [
        form,
        setForm
    ] = useState({
        orderId: "",
        pickupAddress: "",
        pickupCity: "",
        pickupLat: "",
        pickupLng: "",
        dropoffAddress: "",
        dropoffCity: "",
        dropoffLat: "",
        dropoffLng: "",
        mode: "",
        distanceKm: ""
    });


    const loadModes =
        useCallback(
            async () => {
                try {
                    const result =
                        await getDeliveryModes();

                    const modes =
                        Array.isArray(result)
                            ? result
                            : (
                                result?.modes ||
                                result?.data ||
                                []
                            );

                    setDeliveryModes(
                        modes
                    );
                } catch (err) {
                    console.error(
                        "Failed to load delivery modes:",
                        err
                    );

                    setError(
                        err?.message ||
                        "Unable to load delivery modes."
                    );
                }
            },
            []
        );


    const loadRequest =
        useCallback(
            async requestId => {
                if (!requestId) {
                    return null;
                }

                try {
                    const result =
                        await getFulfillmentRequest(
                            requestId
                        );

                    return (
                        result?.request ||
                        result?.data ||
                        result
                    );
                } catch (err) {
                    console.error(
                        "Failed to load fulfillment request:",
                        err
                    );

                    return null;
                }
            },
            []
        );


    const refreshRequest =
        useCallback(
            async requestItem => {
                const requestId =
                    getRequestId(
                        requestItem
                    );

                if (!requestId) {
                    return;
                }

                const fresh =
                    await loadRequest(
                        requestId
                    );

                if (!fresh) {
                    return;
                }

                setRequests(
                    current =>
                        current.map(
                            item =>
                                getRequestId(item) ===
                                requestId
                                    ? fresh
                                    : item
                        )
                );

                setSelectedRequest(
                    fresh
                );
            },
            [loadRequest]
        );


    useEffect(
        () => {
            let cancelled = false;

            async function initialize() {
                setLoading(true);
                setError("");

                try {
                    await loadModes();
                } finally {
                    if (!cancelled) {
                        setLoading(false);
                    }
                }
            }

            initialize();

            return () => {
                cancelled = true;
            };
        },
        [loadModes]
    );


    const updateForm =
        (field, value) => {
            setForm(
                current => ({
                    ...current,
                    [field]: value
                })
            );

            setQuote(null);
            setError("");
            setSuccess("");
        };


    const selectedMode =
        useMemo(
            () =>
                deliveryModes.find(
                    mode =>
                        (
                            mode?.id ||
                            mode?.value ||
                            mode?.code
                        ) === form.mode
                ),
            [
                deliveryModes,
                form.mode
            ]
        );


    const requestList =
        useMemo(
            () =>
                requests.filter(
                    item => {
                        const status =
                            getRequestStatus(
                                item
                            );

                        return (
                            status !==
                                "delivered" &&
                            status !==
                                "cancelled"
                        );
                    }
                ),
            [requests]
        );


    const biddingRequests =
        useMemo(
            () =>
                requestList.filter(
                    item => {
                        const status =
                            getRequestStatus(
                                item
                            );

                        return (
                            status ===
                                "pending" ||
                            status ===
                                "bidding" ||
                            getBids(item).length >
                                0
                        );
                    }
                ),
            [requestList]
        );


    const deliveredItems =
        useMemo(
            () =>
                [
                    ...activeDeliveries,
                    ...history
                ].filter(
                    item =>
                        getDeliveryStatus(
                            item
                        ) === "delivered"
                ),
            [
                activeDeliveries,
                history
            ]
        );


    const activeItems =
        useMemo(
            () =>
                activeDeliveries.filter(
                    item => {
                        const status =
                            getDeliveryStatus(
                                item
                            );

                        return (
                            status !==
                                "delivered" &&
                            status !==
                                "cancelled"
                        );
                    }
                ),
            [activeDeliveries]
        );


    const stats = {
        active:
            activeItems.length,

        bidding:
            biddingRequests.length,

        assigned:
            activeItems.filter(
                item =>
                    [
                        "assigned",
                        "picked_up",
                        "in_transit"
                    ].includes(
                        getDeliveryStatus(
                            item
                        )
                    )
            ).length,

        delivered:
            deliveredItems.length
    };


    function getPayloadCoordinates() {
        const pickup =
            getCoordinates(
                form.pickupLat,
                form.pickupLng
            );

        const dropoff =
            getCoordinates(
                form.dropoffLat,
                form.dropoffLng
            );

        return {
            pickup,
            dropoff
        };
    }


    async function handleQuote() {
        setError("");
        setSuccess("");

        if (
            !form.pickupAddress ||
            !form.dropoffAddress ||
            !form.mode
        ) {
            setError(
                "Pickup address, drop-off address and delivery mode are required."
            );

            return;
        }

        const {
            pickup,
            dropoff
        } =
            getPayloadCoordinates();

        if (
            !pickup ||
            !dropoff
        ) {
            setError(
                "Valid pickup and drop-off coordinates are required."
            );

            return;
        }

        setQuoting(true);

        try {
            const result =
                await calculateQuote({
                    brandId:
                        brand?.id,

                    orderId:
                        form.orderId ||
                        undefined,

                    pickup: {
                        address:
                            form.pickupAddress,

                        city:
                            form.pickupCity,

                        lat:
                            pickup.lat,

                        lng:
                            pickup.lng
                    },

                    dropoff: {
                        address:
                            form.dropoffAddress,

                        city:
                            form.dropoffCity,

                        lat:
                            dropoff.lat,

                        lng:
                            dropoff.lng
                    },

                    /*
                     * The fulfillment API expects
                     * the selected delivery mode as
                     * modeId. Keep mode as well for
                     * compatibility with the API.
                     */
                    modeId:
                        form.mode,

                    mode:
                        form.mode,

                    distanceKm:
                        form.distanceKm
                            ? Number(
                                form.distanceKm
                            )
                            : undefined
                });

            setQuote(
                result?.quote ||
                result?.data ||
                result
            );
        } catch (err) {
            console.error(
                "Fulfillment quote error:",
                err
            );

            setError(
                err?.message ||
                "Unable to calculate delivery quote."
            );
        } finally {
            setQuoting(false);
        }
    }


    async function handleCreateDelivery() {
        setError("");
        setSuccess("");

        /*
         * The fulfillment request API requires
         * an order ID in addition to pickup,
         * drop-off and delivery mode.
         */
        if (
            !form.orderId ||
            !form.pickupAddress ||
            !form.dropoffAddress ||
            !form.mode
        ) {
            setError(
                "Please complete the order, pickup, drop-off and delivery mode fields."
            );

            return;
        }

        const {
            pickup,
            dropoff
        } =
            getPayloadCoordinates();

        if (
            !pickup ||
            !dropoff
        ) {
            setError(
                "Valid pickup and drop-off coordinates are required."
            );

            return;
        }

        setCreating(true);

        try {
            const result =
                await createFulfillmentRequest({
                    brandId:
                        brand?.id,

                    orderId:
                        form.orderId,

                    /*
                     * The fulfillment API expects
                     * the selected delivery mode ID.
                     */
                    modeId:
                        form.mode,

                    pickup: {
                        address:
                            form.pickupAddress,

                        city:
                            form.pickupCity,

                        lat:
                            pickup.lat,

                        lng:
                            pickup.lng
                    },

                    dropoff: {
                        address:
                            form.dropoffAddress,

                        city:
                            form.dropoffCity,

                        lat:
                            dropoff.lat,

                        lng:
                            dropoff.lng
                    },

                    /*
                     * Keep mode as well for compatibility
                     * with the existing fulfillment API.
                     */
                    mode:
                        form.mode,

                    distanceKm:
                        form.distanceKm
                            ? Number(
                                form.distanceKm
                            )
                            : undefined,

                    quote:
                        quote ||
                        undefined
                });

            const created =
                result?.request ||
                result?.data ||
                result;

            if (created) {
                setRequests(
                    current => [
                        created,
                        ...current
                    ]
                );

                setSelectedRequest(
                    created
                );
            }

            setSuccess(
                "Delivery request sent to delivery partners."
            );

            setActiveSection(
                "bids"
            );
        } catch (err) {
            console.error(
                "Create fulfillment request error:",
                err
            );

            setError(
                err?.message ||
                "Unable to create delivery request."
            );
        } finally {
            setCreating(false);
        }
    }


    async function handleAcceptBid(
        request,
        bid
    ) {
        const requestId =
            getRequestId(
                request
            );

        const bidId =
            getBidId(
                bid
            );

        if (
            !requestId ||
            !bidId
        ) {
            setError(
                "The delivery request or bid ID is missing."
            );

            return;
        }

        setError("");
        setSuccess("");

        try {
            const result =
                await acceptBid(
                    requestId,
                    bidId
                );

            const updated =
                result?.request ||
                result?.data ||
                result;

            if (updated) {
                setRequests(
                    current =>
                        current.map(
                            item =>
                                getRequestId(
                                    item
                                ) ===
                                requestId
                                    ? updated
                                    : item
                        )
                );

                setSelectedRequest(
                    updated
                );
            } else {
                await refreshRequest(
                    request
                );
            }

            setSuccess(
                "Bid accepted. The delivery partner has been assigned."
            );

            setActiveSection(
                "active"
            );
        } catch (err) {
            console.error(
                "Accept bid error:",
                err
            );

            setError(
                err?.message ||
                "Unable to accept this bid."
            );
        }
    }


    async function handleStatusUpdate(
        delivery,
        status
    ) {
        const deliveryId =
            getDeliveryId(
                delivery
            );

        if (!deliveryId) {
            setError(
                "Delivery ID is missing."
            );

            return;
        }

        setError("");
        setSuccess("");

        try {
            const result =
                await updateDeliveryStatus(
                    deliveryId,
                    status
                );

            const updated =
                result?.delivery ||
                result?.data ||
                result;

            if (updated) {
                setActiveDeliveries(
                    current =>
                        current.map(
                            item =>
                                getDeliveryId(
                                    item
                                ) ===
                                deliveryId
                                    ? updated
                                    : item
                        )
                );
            }

            setSuccess(
                `Delivery status updated to ${formatStatus(status)}.`
            );
        } catch (err) {
            console.error(
                "Delivery status update error:",
                err
            );

            setError(
                err?.message ||
                "Unable to update delivery status."
            );
        }
    }


    function renderCreateDelivery() {
        return (
            <div
                style={{
                    ...cardStyle,
                    marginTop: 20
                }}
            >
                <div
                    style={{
                        fontSize: 20,
                        fontWeight: 800,
                        marginBottom: 5
                    }}
                >
                    Create delivery
                </div>

                <div
                    style={{
                        color: "#777",
                        fontSize: 14,
                        marginBottom: 24
                    }}
                >
                    Send an order to delivery partners and
                    let them compete with recorded bids.
                </div>


                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns:
                            "repeat(auto-fit, minmax(240px, 1fr))",
                        gap: 18
                    }}
                >
                    <div>
                        <label style={labelStyle}>
                            Order
                        </label>

                        <input
                            style={inputStyle}
                            value={
                                form.orderId
                            }
                            onChange={
                                event =>
                                    updateForm(
                                        "orderId",
                                        event.target.value
                                    )
                            }
                            placeholder="#74mm8zrs68wr628"
                        />
                    </div>


                    <div>
                        <label style={labelStyle}>
                            Delivery mode
                        </label>

                        <select
                            style={inputStyle}
                            value={
                                form.mode
                            }
                            onChange={
                                event =>
                                    updateForm(
                                        "mode",
                                        event.target.value
                                    )
                            }
                        >
                            <option value="">
                                Select mode
                            </option>

                            {deliveryModes.map(
                                mode => {
                                    const id =
                                        mode?.id ||
                                        mode?.value ||
                                        mode?.code;

                                    if (!id) {
                                        return null;
                                    }

                                    return (
                                        <option
                                            key={id}
                                            value={id}
                                        >
                                            {
                                                getModeIcon(
                                                    mode
                                                )
                                            }{" "}
                                            {
                                                getModeLabel(
                                                    mode
                                                )
                                            }
                                        </option>
                                    );
                                }
                            )}
                        </select>
                    </div>


                    <div>
                        <label style={labelStyle}>
                            Estimated distance (km)
                        </label>

                        <input
                            style={inputStyle}
                            type="number"
                            min="0"
                            step="0.1"
                            value={
                                form.distanceKm
                            }
                            onChange={
                                event =>
                                    updateForm(
                                        "distanceKm",
                                        event.target.value
                                    )
                            }
                            placeholder="7.8"
                        />
                    </div>
                </div>


                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns:
                            "repeat(auto-fit, minmax(280px, 1fr))",
                        gap: 18,
                        marginTop: 20
                    }}
                >

                    {/* Pickup */}

                    <div
                        style={{
                            border:
                                "1px solid #eee",
                            borderRadius: 14,
                            padding: 18
                        }}
                    >
                        <div
                            style={{
                                fontWeight: 800,
                                marginBottom: 14
                            }}
                        >
                            Pickup
                        </div>


                        <div
                            style={{
                                marginBottom: 12
                            }}
                        >
                            <label style={labelStyle}>
                                Address
                            </label>

                            <input
                                style={inputStyle}
                                value={
                                    form.pickupAddress
                                }
                                onChange={
                                    event =>
                                        updateForm(
                                            "pickupAddress",
                                            event.target.value
                                        )
                                }
                                placeholder="Brand warehouse"
                            />
                        </div>


                        <div
                            style={{
                                marginBottom: 12
                            }}
                        >
                            <label style={labelStyle}>
                                City
                            </label>

                            <input
                                style={inputStyle}
                                value={
                                    form.pickupCity
                                }
                                onChange={
                                    event =>
                                        updateForm(
                                            "pickupCity",
                                            event.target.value
                                        )
                                }
                                placeholder="Kolkata"
                            />
                        </div>


                        <div
                            style={{
                                marginBottom: 12
                            }}
                        >
                            <label style={labelStyle}>
                                Latitude
                            </label>

                            <input
                                style={inputStyle}
                                value={
                                    form.pickupLat
                                }
                                onChange={
                                    event =>
                                        updateForm(
                                            "pickupLat",
                                            event.target.value
                                        )
                                }
                                placeholder="22.5680 or 22.5680° N"
                            />
                        </div>


                        <div>
                            <label style={labelStyle}>
                                Longitude
                            </label>

                            <input
                                style={inputStyle}
                                value={
                                    form.pickupLng
                                }
                                onChange={
                                    event =>
                                        updateForm(
                                            "pickupLng",
                                            event.target.value
                                        )
                                }
                                placeholder="88.3711 or 88.3711° E"
                            />
                        </div>
                    </div>


                    {/* Drop-off */}

                    <div
                        style={{
                            border:
                                "1px solid #eee",
                            borderRadius: 14,
                            padding: 18
                        }}
                    >
                        <div
                            style={{
                                fontWeight: 800,
                                marginBottom: 14
                            }}
                        >
                            Drop-off
                        </div>


                        <div
                            style={{
                                marginBottom: 12
                            }}
                        >
                            <label style={labelStyle}>
                                Customer address
                            </label>

                            <input
                                style={inputStyle}
                                value={
                                    form.dropoffAddress
                                }
                                onChange={
                                    event =>
                                        updateForm(
                                            "dropoffAddress",
                                            event.target.value
                                        )
                                }
                                placeholder="Customer address"
                            />
                        </div>


                        <div
                            style={{
                                marginBottom: 12
                            }}
                        >
                            <label style={labelStyle}>
                                City
                            </label>

                            <input
                                style={inputStyle}
                                value={
                                    form.dropoffCity
                                }
                                onChange={
                                    event =>
                                        updateForm(
                                            "dropoffCity",
                                            event.target.value
                                        )
                                }
                                placeholder="Kolkata"
                            />
                        </div>


                        <div
                            style={{
                                marginBottom: 12
                            }}
                        >
                            <label style={labelStyle}>
                                Latitude
                            </label>

                            <input
                                style={inputStyle}
                                value={
                                    form.dropoffLat
                                }
                                onChange={
                                    event =>
                                        updateForm(
                                            "dropoffLat",
                                            event.target.value
                                        )
                                }
                                placeholder="22.723940 or 22.723940° N"
                            />
                        </div>


                        <div>
                            <label style={labelStyle}>
                                Longitude
                            </label>

                            <input
                                style={inputStyle}
                                value={
                                    form.dropoffLng
                                }
                                onChange={
                                    event =>
                                        updateForm(
                                            "dropoffLng",
                                            event.target.value
                                        )
                                }
                                placeholder="88.483665 or 88.483665° E"
                            />
                        </div>
                    </div>
                </div>


                {selectedMode && (
                    <div
                        style={{
                            marginTop: 18,
                            padding: 14,
                            borderRadius: 12,
                            background: "#f7f7f7",
                            fontSize: 14
                        }}
                    >
                        Selected mode:{" "}
                        <strong>
                            {
                                getModeIcon(
                                    selectedMode
                                )
                            }{" "}
                            {
                                getModeLabel(
                                    selectedMode
                                )
                            }
                        </strong>
                    </div>
                )}


                {quote && (
                    <div
                        style={{
                            marginTop: 20,
                            border:
                                "1px solid #e8e8e8",
                            borderRadius: 14,
                            padding: 18
                        }}
                    >
                        <div
                            style={{
                                fontWeight: 800,
                                marginBottom: 14
                            }}
                        >
                            Delivery quote
                        </div>

                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns:
                                    "repeat(auto-fit, minmax(160px, 1fr))",
                                gap: 14
                            }}
                        >
                            <div>
                                <div
                                    style={{
                                        fontSize: 12,
                                        color: "#777"
                                    }}
                                >
                                    Estimated distance
                                </div>

                                <strong>
                                    {
                                        quote.distanceKm ??
                                        quote.distance ??
                                        form.distanceKm ??
                                        "—"
                                    }{" "}
                                    km
                                </strong>
                            </div>


                            <div>
                                <div
                                    style={{
                                        fontSize: 12,
                                        color: "#777"
                                    }}
                                >
                                    Base payout
                                </div>

                                <strong>
                                    {
                                        formatCurrency(
                                            quote.basePayout ??
                                            quote.base ??
                                            0
                                        )
                                    }
                                </strong>
                            </div>


                            <div>
                                <div
                                    style={{
                                        fontSize: 12,
                                        color: "#777"
                                    }}
                                >
                                    Dynamic offer
                                </div>

                                <strong>
                                    {
                                        formatCurrency(
                                            quote.dynamicOffer ??
                                            quote.offer ??
                                            quote.payout ??
                                            quote.amount ??
                                            0
                                        )
                                    }
                                </strong>
                            </div>
                        </div>
                    </div>
                )}


                <div
                    style={{
                        display: "flex",
                        gap: 10,
                        justifyContent: "flex-end",
                        flexWrap: "wrap",
                        marginTop: 22
                    }}
                >
                    <button
                        type="button"
                        style={
                            secondaryButtonStyle
                        }
                        onClick={
                            handleQuote
                        }
                        disabled={
                            quoting
                        }
                    >
                        {
                            quoting
                                ? "Calculating..."
                                : "Calculate quote"
                        }
                    </button>


                    <button
                        type="button"
                        style={
                            primaryButtonStyle
                        }
                        onClick={
                            handleCreateDelivery
                        }
                        disabled={
                            creating
                        }
                    >
                        {
                            creating
                                ? "Sending..."
                                : "Send to delivery partners"
                        }
                    </button>
                </div>
            </div>
        );
    }


    function renderBids() {
        if (!biddingRequests.length) {
            return (
                <div
                    style={{
                        ...cardStyle,
                        marginTop: 20
                    }}
                >
                    <EmptyState
                        title="No open bids"
                        text="Delivery requests with active partner bids will appear here."
                    />
                </div>
            );
        }

        return (
            <div
                style={{
                    display: "grid",
                    gap: 18,
                    marginTop: 20
                }}
            >
                {biddingRequests.map(
                    request => {
                        const bids =
                            getBids(
                                request
                            );

                        return (
                            <div
                                key={
                                    getRequestId(
                                        request
                                    ) ||
                                    Math.random()
                                }
                                style={
                                    cardStyle
                                }
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        gap: 15,
                                        flexWrap: "wrap",
                                        marginBottom: 18
                                    }}
                                >
                                    <div>
                                        <div
                                            style={{
                                                fontWeight: 800,
                                                fontSize: 17
                                            }}
                                        >
                                            DELIVERY BIDS
                                        </div>

                                        <div
                                            style={{
                                                color: "#777",
                                                fontSize: 13,
                                                marginTop: 4
                                            }}
                                        >
                                            Request{" "}
                                            {
                                                getRequestId(
                                                    request
                                                ) ||
                                                "—"
                                            }
                                        </div>
                                    </div>

                                    <div
                                        style={{
                                            fontSize: 13,
                                            fontWeight: 700
                                        }}
                                    >
                                        {
                                            formatStatus(
                                                getRequestStatus(
                                                    request
                                                )
                                            )
                                        }
                                    </div>
                                </div>


                                {!bids.length ? (
                                    <EmptyState
                                        title="Waiting for partners"
                                        text="No bids have been recorded yet."
                                    />
                                ) : (
                                    <div
                                        style={{
                                            display: "grid",
                                            gap: 12
                                        }}
                                    >
                                        {bids.map(
                                            bid => {
                                                const amount =
                                                    getBidAmount(
                                                        bid
                                                    );

                                                return (
                                                    <div
                                                        key={
                                                            getBidId(
                                                                bid
                                                            ) ||
                                                            Math.random()
                                                        }
                                                        style={{
                                                            border:
                                                                "1px solid #eee",
                                                            borderRadius:
                                                                14,
                                                            padding:
                                                                17,
                                                            display:
                                                                "flex",
                                                            justifyContent:
                                                                "space-between",
                                                            alignItems:
                                                                "center",
                                                            gap:
                                                                18,
                                                            flexWrap:
                                                                "wrap"
                                                        }}
                                                    >
                                                        <div>
                                                            <div
                                                                style={{
                                                                    fontWeight:
                                                                        800,
                                                                    fontSize:
                                                                        16
                                                                }}
                                                            >
                                                                {
                                                                    getPartnerName(
                                                                        bid
                                                                    )
                                                                }
                                                            </div>

                                                            <div
                                                                style={{
                                                                    color:
                                                                        "#777",
                                                                    fontSize:
                                                                        13,
                                                                    marginTop:
                                                                        5
                                                                }}
                                                            >
                                                                {
                                                                    getModeIcon(
                                                                        bid?.mode ||
                                                                        bid?.vehicleType ||
                                                                        bid?.vehicle_type ||
                                                                        ""
                                                                    )
                                                                }{" "}
                                                                {
                                                                    getModeLabel(
                                                                        bid?.mode ||
                                                                        bid?.vehicleType ||
                                                                        bid?.vehicle_type
                                                                    )
                                                                }
                                                            </div>

                                                            <div
                                                                style={{
                                                                    display:
                                                                        "flex",
                                                                    gap:
                                                                        14,
                                                                    flexWrap:
                                                                        "wrap",
                                                                    marginTop:
                                                                        8,
                                                                    fontSize:
                                                                        13
                                                                }}
                                                            >
                                                                {
                                                                    getPartnerRating(
                                                                        bid
                                                                    ) !==
                                                                        null && (
                                                                        <span>
                                                                            Rating{" "}
                                                                            <strong>
                                                                                {
                                                                                    getPartnerRating(
                                                                                        bid
                                                                                    )
                                                                                }
                                                                            </strong>
                                                                        </span>
                                                                    )
                                                                }

                                                                {
                                                                    getBidEta(
                                                                        bid
                                                                    ) !==
                                                                        null && (
                                                                        <span>
                                                                            ETA{" "}
                                                                            <strong>
                                                                                {
                                                                                    getBidEta(
                                                                                        bid
                                                                                    )
                                                                                }{" "}
                                                                                min
                                                                            </strong>
                                                                        </span>
                                                                    )
                                                                }
                                                            </div>

                                                            {
                                                                (
                                                                    bid?.message ||
                                                                    bid?.note
                                                                ) && (
                                                                    <div
                                                                        style={{
                                                                            marginTop:
                                                                                10,
                                                                            fontSize:
                                                                                13,
                                                                            color:
                                                                                "#555"
                                                                        }}
                                                                    >
                                                                        “
                                                                        {
                                                                            bid.message ||
                                                                            bid.note
                                                                        }
                                                                        ”
                                                                    </div>
                                                                )
                                                            }
                                                        </div>


                                                        <div
                                                            style={{
                                                                textAlign:
                                                                    "right"
                                                            }}
                                                        >
                                                            <div
                                                                style={{
                                                                    fontSize:
                                                                        21,
                                                                    fontWeight:
                                                                        800,
                                                                    marginBottom:
                                                                        8
                                                                }}
                                                            >
                                                                {
                                                                    formatCurrency(
                                                                        amount
                                                                    )
                                                                }
                                                            </div>

                                                            <button
                                                                type="button"
                                                                style={
                                                                    primaryButtonStyle
                                                                }
                                                                onClick={() =>
                                                                    handleAcceptBid(
                                                                        request,
                                                                        bid
                                                                    )
                                                                }
                                                            >
                                                                Accept{" "}
                                                                {
                                                                    formatCurrency(
                                                                        amount
                                                                    )
                                                                }
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            }
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    }
                )}
            </div>
        );
    }


    function renderActiveDeliveries() {
        if (!activeItems.length) {
            return (
                <div
                    style={{
                        ...cardStyle,
                        marginTop: 20
                    }}
                >
                    <EmptyState
                        title="No active deliveries"
                        text="Assigned and in-progress deliveries will appear here."
                    />
                </div>
            );
        }

        return (
            <div
                style={{
                    display: "grid",
                    gap: 15,
                    marginTop: 20
                }}
            >
                {activeItems.map(
                    delivery => {
                        const status =
                            getDeliveryStatus(
                                delivery
                            );

                        return (
                            <div
                                key={
                                    getDeliveryId(
                                        delivery
                                    ) ||
                                    Math.random()
                                }
                                style={
                                    cardStyle
                                }
                            >
                                <div
                                    style={{
                                        display:
                                            "flex",
                                        justifyContent:
                                            "space-between",
                                        gap:
                                            15,
                                        flexWrap:
                                            "wrap"
                                    }}
                                >
                                    <div>
                                        <div
                                            style={{
                                                fontWeight:
                                                    800,
                                                fontSize:
                                                    17
                                            }}
                                        >
                                            Delivery{" "}
                                            {
                                                getDeliveryId(
                                                    delivery
                                                ) ||
                                                "—"
                                            }
                                        </div>

                                        <div
                                            style={{
                                                color:
                                                    "#777",
                                                fontSize:
                                                    13,
                                                marginTop:
                                                    5
                                            }}
                                        >
                                            Order{" "}
                                            {
                                                delivery?.orderId ||
                                                delivery?.order_id ||
                                                "—"
                                            }
                                        </div>
                                    </div>

                                    <div
                                        style={{
                                            padding:
                                                "7px 11px",
                                            borderRadius:
                                                20,
                                            background:
                                                "#f3f3f3",
                                            fontSize:
                                                12,
                                            fontWeight:
                                                800
                                        }}
                                    >
                                        {
                                            formatStatus(
                                                status
                                            )
                                        }
                                    </div>
                                </div>


                                <div
                                    style={{
                                        display:
                                            "grid",
                                        gridTemplateColumns:
                                            "repeat(auto-fit, minmax(180px, 1fr))",
                                        gap:
                                            16,
                                        marginTop:
                                            20
                                    }}
                                >
                                    <div>
                                        <div
                                            style={{
                                                fontSize:
                                                    12,
                                                color:
                                                    "#777"
                                            }}
                                        >
                                            Partner
                                        </div>

                                        <strong>
                                            {
                                                delivery?.partner?.name ||
                                                delivery?.partnerName ||
                                                delivery?.partner_name ||
                                                "Assigned partner"
                                            }
                                        </strong>
                                    </div>


                                    <div>
                                        <div
                                            style={{
                                                fontSize:
                                                    12,
                                                color:
                                                    "#777"
                                            }}
                                        >
                                            Mode
                                        </div>

                                        <strong>
                                            {
                                                getModeIcon(
                                                    delivery?.mode
                                                )
                                            }{" "}
                                            {
                                                getModeLabel(
                                                    delivery?.mode
                                                )
                                            }
                                        </strong>
                                    </div>


                                    <div>
                                        <div
                                            style={{
                                                fontSize:
                                                    12,
                                                color:
                                                    "#777"
                                            }}
                                        >
                                            Payout
                                        </div>

                                        <strong>
                                            {
                                                formatCurrency(
                                                    delivery?.payout ||
                                                    delivery?.amount ||
                                                    0
                                                )
                                            }
                                        </strong>
                                    </div>
                                </div>


                                <div
                                    style={{
                                        display:
                                            "flex",
                                        gap:
                                            9,
                                        flexWrap:
                                            "wrap",
                                        marginTop:
                                            20
                                    }}
                                >
                                    {status ===
                                        "assigned" && (
                                        <button
                                            type="button"
                                            style={
                                                secondaryButtonStyle
                                            }
                                            onClick={() =>
                                                handleStatusUpdate(
                                                    delivery,
                                                    "picked_up"
                                                )
                                            }
                                        >
                                            Mark picked up
                                        </button>
                                    )}


                                    {status ===
                                        "picked_up" && (
                                        <button
                                            type="button"
                                            style={
                                                secondaryButtonStyle
                                            }
                                            onClick={() =>
                                                handleStatusUpdate(
                                                    delivery,
                                                    "in_transit"
                                                )
                                            }
                                        >
                                            Mark in transit
                                        </button>
                                    )}


                                    {status ===
                                        "in_transit" && (
                                        <button
                                            type="button"
                                            style={
                                                primaryButtonStyle
                                            }
                                            onClick={() =>
                                                handleStatusUpdate(
                                                    delivery,
                                                    "delivered"
                                                )
                                            }
                                        >
                                            Mark delivered
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    }
                )}
            </div>
        );
    }


    function renderHistory() {
        if (!history.length) {
            return (
                <div
                    style={{
                        ...cardStyle,
                        marginTop: 20
                    }}
                >
                    <EmptyState
                        title="No delivery history"
                        text="Completed deliveries will appear here."
                    />
                </div>
            );
        }

        return (
            <div
                style={{
                    ...cardStyle,
                    marginTop: 20,
                    overflowX: "auto"
                }}
            >
                <table
                    style={{
                        width: "100%",
                        borderCollapse:
                            "collapse",
                        minWidth: 650
                    }}
                >
                    <thead>
                        <tr>
                            {[
                                [
                                    "Delivery",
                                    "left"
                                ],
                                [
                                    "Order",
                                    "left"
                                ],
                                [
                                    "Partner",
                                    "left"
                                ],
                                [
                                    "Mode",
                                    "left"
                                ],
                                [
                                    "Payout",
                                    "right"
                                ]
                            ].map(
                                ([
                                    label,
                                    align
                                ]) => (
                                    <th
                                        key={
                                            label
                                        }
                                        style={{
                                            textAlign:
                                                align,
                                            padding:
                                                12,
                                            borderBottom:
                                                "1px solid #eee",
                                            fontSize:
                                                12,
                                            color:
                                                "#777"
                                        }}
                                    >
                                        {label}
                                    </th>
                                )
                            )}
                        </tr>
                    </thead>

                    <tbody>
                        {history.map(
                            delivery => (
                                <tr
                                    key={
                                        getDeliveryId(
                                            delivery
                                        ) ||
                                        Math.random()
                                    }
                                >
                                    <td
                                        style={{
                                            padding:
                                                12,
                                            borderBottom:
                                                "1px solid #f0f0f0",
                                            fontWeight:
                                                700
                                        }}
                                    >
                                        {
                                            getDeliveryId(
                                                delivery
                                            ) ||
                                            "—"
                                        }
                                    </td>

                                    <td
                                        style={{
                                            padding:
                                                12,
                                            borderBottom:
                                                "1px solid #f0f0f0"
                                        }}
                                    >
                                        {
                                            delivery?.orderId ||
                                            delivery?.order_id ||
                                            "—"
                                        }
                                    </td>

                                    <td
                                        style={{
                                            padding:
                                                12,
                                            borderBottom:
                                                "1px solid #f0f0f0"
                                        }}
                                    >
                                        {
                                            delivery?.partner?.name ||
                                            delivery?.partnerName ||
                                            delivery?.partner_name ||
                                            "—"
                                        }
                                    </td>

                                    <td
                                        style={{
                                            padding:
                                                12,
                                            borderBottom:
                                                "1px solid #f0f0f0"
                                        }}
                                    >
                                        {
                                            getModeIcon(
                                                delivery?.mode
                                            )
                                        }{" "}
                                        {
                                            getModeLabel(
                                                delivery?.mode
                                            )
                                        }
                                    </td>

                                    <td
                                        style={{
                                            padding:
                                                12,
                                            borderBottom:
                                                "1px solid #f0f0f0",
                                            textAlign:
                                                "right",
                                            fontWeight:
                                                800
                                        }}
                                    >
                                        {
                                            formatCurrency(
                                                delivery?.payout ||
                                                delivery?.amount ||
                                                0
                                            )
                                        }
                                    </td>
                                </tr>
                            )
                        )}
                    </tbody>
                </table>
            </div>
        );
    }


    function renderOverview() {
        return (
            <>
                <div
                    style={{
                        display:
                            "grid",
                        gridTemplateColumns:
                            "repeat(4, minmax(0, 1fr))",
                        gap:
                            14,
                        marginTop:
                            20
                    }}
                >
                    <StatCard
                        label="Active"
                        value={
                            stats.active
                        }
                    />

                    <StatCard
                        label="Bidding"
                        value={
                            stats.bidding
                        }
                    />

                    <StatCard
                        label="Assigned"
                        value={
                            stats.assigned
                        }
                    />

                    <StatCard
                        label="Delivered"
                        value={
                            stats.delivered
                        }
                    />
                </div>

                {
                    renderCreateDelivery()
                }
            </>
        );
    }


    if (loading) {
        return (
            <div
                style={{
                    padding: 30,
                    color: "#777"
                }}
            >
                Loading fulfillment...
            </div>
        );
    }


    return (
        <div
            style={{
                width: "100%",
                boxSizing: "border-box",
                padding:
                    "26px 28px 40px",
                background:
                    "#fafafa",
                minHeight:
                    "100%"
            }}
        >
            <div
                style={{
                    maxWidth: 1400,
                    margin: "0 auto"
                }}
            >
                <div
                    style={{
                        display:
                            "flex",
                        justifyContent:
                            "space-between",
                        alignItems:
                            "flex-start",
                        gap:
                            20,
                        flexWrap:
                            "wrap"
                    }}
                >
                    <div>
                        <h1
                            style={{
                                margin: 0,
                                fontSize: 28,
                                lineHeight:
                                    1.15,
                                fontWeight:
                                    850,
                                color:
                                    "#111"
                            }}
                        >
                            Fulfillment
                        </h1>

                        <div
                            style={{
                                marginTop:
                                    7,
                                color:
                                    "#777",
                                fontSize:
                                    14
                            }}
                        >
                            Manage every last-mile delivery
                            from one place.
                        </div>
                    </div>


                    <button
                        type="button"
                        style={
                            primaryButtonStyle
                        }
                        onClick={() => {
                            setActiveSection(
                                "create"
                            );

                            setError("");
                            setSuccess("");
                        }}
                    >
                        + Create delivery
                    </button>
                </div>


                {(error ||
                    success) && (
                    <div
                        style={{
                            marginTop:
                                18,
                            padding:
                                13,
                            borderRadius:
                                11,
                            background:
                                error
                                    ? "#fff1f1"
                                    : "#effaf2",
                            color:
                                error
                                    ? "#b42318"
                                    : "#18753c",
                            fontSize:
                                14,
                            fontWeight:
                                600
                        }}
                    >
                        {
                            error ||
                            success
                        }
                    </div>
                )}


                <div
                    style={{
                        display:
                            "flex",
                        gap:
                            8,
                        flexWrap:
                            "wrap",
                        marginTop:
                            22,
                        paddingBottom:
                            2
                    }}
                >
                    {[
                        [
                            "overview",
                            "Overview"
                        ],
                        [
                            "create",
                            "Create Delivery"
                        ],
                        [
                            "bids",
                            "Open Bids"
                        ],
                        [
                            "active",
                            "Active Deliveries"
                        ],
                        [
                            "history",
                            "Delivery History"
                        ]
                    ].map(
                        ([
                            id,
                            label
                        ]) => (
                            <button
                                key={
                                    id
                                }
                                type="button"
                                onClick={() =>
                                    setActiveSection(
                                        id
                                    )
                                }
                                style={{
                                    border:
                                        activeSection ===
                                        id
                                            ? "1px solid #111"
                                            : "1px solid #ddd",
                                    background:
                                        activeSection ===
                                        id
                                            ? "#111"
                                            : "#fff",
                                    color:
                                        activeSection ===
                                        id
                                            ? "#fff"
                                            : "#444",
                                    borderRadius:
                                        10,
                                    padding:
                                        "9px 13px",
                                    cursor:
                                        "pointer",
                                    fontSize:
                                        13,
                                    fontWeight:
                                        700
                                }}
                            >
                                {label}
                            </button>
                        )
                    )}
                </div>


                {
                    activeSection ===
                        "overview" &&
                    renderOverview()
                }

                {
                    activeSection ===
                        "create" &&
                    renderCreateDelivery()
                }

                {
                    activeSection ===
                        "bids" &&
                    renderBids()
                }

                {
                    activeSection ===
                        "active" &&
                    renderActiveDeliveries()
                }

                {
                    activeSection ===
                        "history" &&
                    renderHistory()
                }
            </div>
        </div>
    );
}

